'use client';

import * as React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import AuthButton from '@/components/auth/AuthButton';
import { cn } from '@/lib/utils';
import { showEventTickets } from '@/lib/features';
import CurrencySelector from './CurrencySelector';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Menu, UserPlus, Info, Mail, Briefcase, ChevronDown, ChevronLeft } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuthUser } from '@/hooks/use-auth-user';
import { useIsNativeApp } from '@/hooks/use-native-app';
import { editorBandSurface } from '@/lib/editorial';

const bandBg = 'var(--nl-editor-band-bg)';
const bandFg = 'var(--nl-editor-band-fg)';

/** Gold + cream interactions — unchanged from previous header */
const gold = 'text-[#daba7b]';

/**
 * The pages the app reaches from the tab bar. Anywhere else the app header shows a back button:
 * the app has no browser buttons, so without it a hotel or an experience could only be left
 * through the tab bar.
 */
const APP_ROOT_PAGES = ['/', '/hotels', '/bookings', '/initiatives', '/account', '/admin', '/agent-dashboard', '/auth/login'];
const hoverSurface = 'hover:bg-white/20';
const hoverSurfaceSoft = 'hover:bg-white/10';

const navLinkClass = cn(
  'rounded-none px-3 py-2.5 text-[13px] font-medium tracking-wide transition-colors',
  gold,
  hoverSurface,
);

/** Same typography as nav links — signed-in “Our Services” is a dropdown, not a different visual tier */
const navTriggerClass = cn(
  'inline-flex h-10 items-center gap-2 rounded-none px-3 py-2.5 text-[13px] font-medium tracking-wide transition-colors',
  gold,
  hoverSurface,
);

const sheetRowClass = cn(
  'flex items-center gap-3 border-b border-white/10 px-4 py-3.5 text-[15px] transition-colors last:border-b-0',
  gold,
  hoverSurfaceSoft,
);

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const isHotelsPage = pathname?.startsWith('/hotels') || pathname?.startsWith('/bookings/hotels');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isServicesOpen, setIsServicesOpen] = React.useState(false);
  const authUser = useAuthUser();
  const user = authUser ?? null;
  const isLoading = authUser === undefined;
  // The app shows only the pages behind the login, so the marketing links stay out of it.
  const isNative = useIsNativeApp();
  const showBack = isNative && !!pathname && !APP_ROOT_PAGES.includes(pathname);

  const goBack = React.useCallback(() => {
    // A page opened from a link (an email, a notification) has nothing to go back to.
    if (window.history.length > 1) router.back();
    else router.push('/');
  }, [router]);

  const closeMobile = React.useCallback(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  }, []);

  return (
    <header
      className="sticky top-0 z-50 w-full border-b border-white/10 pt-[env(safe-area-inset-top)]"
      style={editorBandSurface}
    >
      <div className="mx-auto flex h-[72px] w-full max-w-[1280px] items-stretch justify-between gap-3 px-5 md:px-8 lg:px-14">
        {/* Logo */}
        <div className="flex flex-shrink-0 items-center">
          {showBack && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={goBack}
              aria-label="Back"
              className={cn('-ml-2 mr-1 h-11 w-11 rounded-none', gold, hoverSurface)}
            >
              <ChevronLeft className="h-6 w-6" aria-hidden />
            </Button>
          )}
          <Link
            href="/"
            className="flex items-center opacity-95 transition-opacity hover:opacity-100"
            aria-label="Nordic LuxAdvisor home"
          >
            <Image src="/logo.png" alt="Nordic Luxadvisor Logo" width={160} height={40} className="h-9 w-auto md:h-10" />
          </Link>
        </div>

        {/* Rail */}
        <div className="flex min-w-0 flex-1 items-center justify-end gap-1 sm:gap-2 md:gap-3">
          {!isLoading && user && (
            <nav className="hidden min-w-0 items-center xl:flex xl:gap-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className={navTriggerClass}>
                    Our Services
                    <ChevronDown className="h-3.5 w-3.5 opacity-80" aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="min-w-[240px] rounded-none border border-white/20 p-0"
                  style={{ backgroundColor: bandBg, color: bandFg }}
                >
                  <DropdownMenuItem asChild className="rounded-none border-b border-white/10 px-4 py-3 focus:bg-white/10">
                    <Link href="/hotels" className={cn('cursor-pointer text-[14px]', gold)}>
                      Hotel Booking Platform
                    </Link>
                  </DropdownMenuItem>
                  {showEventTickets && (
                  <DropdownMenuItem asChild className="rounded-none border-b border-white/10 px-4 py-3 focus:bg-white/10">
                    <Link href="/event/tickets" className={cn('cursor-pointer text-[14px]', gold)}>
                      Event Tickets
                    </Link>
                  </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild className="rounded-none border-b border-white/10 px-4 py-3 focus:bg-white/10">
                    <Link href="/initiatives" className={cn('cursor-pointer text-[14px]', gold)}>
                      Curated Experiences
                    </Link>
                  </DropdownMenuItem>
                  {!isNative && (
                  <DropdownMenuItem asChild className="rounded-none px-4 py-3 focus:bg-white/10">
                    <Link href="/services" className={cn('cursor-pointer text-[14px]', gold)}>
                      Concierge Services
                    </Link>
                  </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {!isNative && (
                <>
                  <Link href="/contact" className={navLinkClass}>
                    Contact & Inquiries
                  </Link>
                  <Link href="/about" className={navLinkClass}>
                    About Us
                  </Link>
                </>
              )}
            </nav>
          )}

          {!isLoading && !user && !isNative && (
            <nav className="hidden items-center xl:flex xl:gap-0">
              <Link href="/services" className={navLinkClass}>
                Our Services
              </Link>
              <Link href="/membership" className={navLinkClass}>
                Memberships
              </Link>
              <Link href="/contact" className={navLinkClass}>
                Contact & Inquiries
              </Link>
              <Link href="/about" className={navLinkClass}>
                About Us
              </Link>
            </nav>
          )}

          <div className="hidden items-center gap-3 md:flex">
            {!isLoading && user && isHotelsPage && <CurrencySelector />}
            <AuthButton />
          </div>

          <div className="flex items-center xl:hidden">
            <Sheet
              open={isMobileMenuOpen}
              onOpenChange={(open) => {
                setIsMobileMenuOpen(open);
                if (!open) setIsServicesOpen(false);
              }}
            >
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className={cn('rounded-none', gold, hoverSurface)}>
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="flex w-full max-w-[min(100vw,380px)] flex-col border-white/20 p-0 pt-[calc(3.5rem+env(safe-area-inset-top))] sm:max-w-md"
                style={{ backgroundColor: bandBg, color: bandFg }}
              >
                <SheetHeader className="sr-only">
                  <SheetTitle>Mobile Menu</SheetTitle>
                </SheetHeader>

                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                  {!isLoading && user && (
                    <>
                      <div className="border-b border-white/10">
                        <button
                          type="button"
                          onClick={() => setIsServicesOpen(!isServicesOpen)}
                          className={cn(
                            'flex w-full items-center justify-between px-4 py-3.5 text-left text-[15px] transition-colors',
                            gold,
                            hoverSurfaceSoft,
                          )}
                        >
                          <span className="flex items-center gap-3">
                            <Briefcase className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                            Our Services
                          </span>
                          <ChevronDown
                            className={cn('h-4 w-4 shrink-0 transition-transform', isServicesOpen && 'rotate-180')}
                            aria-hidden
                          />
                        </button>
                        {isServicesOpen && (
                          <div className="border-t border-white/10 bg-black/10">
                            <Link href="/hotels" className={sheetRowClass} onClick={closeMobile}>
                              Hotel Booking Platform
                            </Link>
                            {showEventTickets && (
                            <Link href="/event/tickets" className={sheetRowClass} onClick={closeMobile}>
                              Event Tickets
                            </Link>
                            )}
                            <Link href="/initiatives" className={sheetRowClass} onClick={closeMobile}>
                              Curated Experiences
                            </Link>
                            {!isNative && (
                            <Link href="/services" className={sheetRowClass} onClick={closeMobile}>
                              Concierge Services
                            </Link>
                            )}
                          </div>
                        )}
                      </div>

                      {!isNative && (
                        <>
                          <Link href="/contact" className={sheetRowClass} onClick={closeMobile}>
                            <Mail className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                            Contact & Inquiries
                          </Link>
                          <Link href="/about" className={sheetRowClass} onClick={closeMobile}>
                            <Info className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                            About Us
                          </Link>
                        </>
                      )}
                    </>
                  )}

                  {!isLoading && !user && !isNative && (
                    <>
                      <Link href="/services" className={sheetRowClass} onClick={closeMobile}>
                        <Briefcase className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                        Our Services
                      </Link>
                      <Link href="/membership" className={sheetRowClass} onClick={closeMobile}>
                        <UserPlus className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                        Memberships
                      </Link>
                      <Link href="/contact" className={sheetRowClass} onClick={closeMobile}>
                        <Mail className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                        Contact & Inquiries
                      </Link>
                      <Link href="/about" className={sheetRowClass} onClick={closeMobile}>
                        <Info className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                        About Us
                      </Link>
                    </>
                  )}
                </div>

                {/* Below md the signed-in account actions live in the bottom tab bar
                    (MobileTabBar), so the AuthButton is redundant there — but the tab
                    bar doesn't exist at md–xl, where this sheet is still the only nav. */}
                <div
                  className={cn(
                    'mt-auto border-t border-white/10 px-4 py-5',
                    user && !isHotelsPage && 'hidden md:block',
                  )}
                >
                  {!isLoading && user && isHotelsPage && (
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <span className={cn('text-lg', gold)}>Currency</span>
                      <CurrencySelector />
                    </div>
                  )}
                  <div onClick={closeMobile} className={cn('w-full', user && 'hidden md:block')}>
                    <AuthButton />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
